import { useInfiniteQuery } from '@tanstack/react-query';

import { queryKeyFactory } from 'lib/utils/queryKeyFactory';
import { useEffect } from 'react';
import { baseInfiniteQueryOptions } from './baseInfiniteQueryOptions';
import { listPatients, listPatientsWithAssessmentStatus } from 'lib/api/firebase/apis/apis';

/**
 *
 * WARNING: The queryFn is invoked multiple times after the returned cursor is `null`.
 *  These are redundant requests to the backend
 *
 * @param clinicId
 * @param pendingUsers
 * @returns
 */
export function useGetPatientsList(
  clinicId: string,
  pendingUsers: boolean = false
) {
  const query = useInfiniteQuery({
    ...baseInfiniteQueryOptions(clinicId),
    queryKey: queryKeyFactory.listPatients(clinicId, pendingUsers),
    queryFn: ({ pageParam }) =>
      listPatients({
        pageSize: 100,
        clinicId: clinicId,
        pendingUsers,
        ...(pageParam !== undefined ? { cursor: pageParam } : undefined)
      })
  });
  /**
   * Fetch the next page as soon as the first one is loaded
   */
  useEffect(
    () => {
      if (query.hasNextPage && query.isFetched) {
        query.fetchNextPage();
      }
    },
    // eslint-disable-next-line
    [query.hasNextPage, query.isFetched, query.isFetching]
  );
  return query;
}

export function useGetPatientListWithAssessmentData({
  clinicId,
  pendingUsers = false
}: {
  clinicId: string;
  pendingUsers?: boolean;
}) {
  const query = useInfiniteQuery({
    ...baseInfiniteQueryOptions(clinicId),
    queryKey: queryKeyFactory.listPatientsWithAssessmentStatus(
      clinicId,
      pendingUsers
    ),
    queryFn: ({ pageParam }) =>
      listPatientsWithAssessmentStatus({
        clinicId,
        pageSize: 1000,
        pendingUsers,
        ...(pageParam !== undefined ? { cursor: pageParam } : undefined)
      })
  });
  useEffect(
    () => {
      if (query.hasNextPage && query.isFetched) {
        query.fetchNextPage();
      }
    },
    // eslint-disable-next-line
    [query.hasNextPage, query.isFetched, query.isFetching]
  );

  return query;
}
